import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ResponsePayload } from '../../../types';
import { RatingQuestion } from '../../questions/RatingQuestion';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';

const clampRating = (value: number | null): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  if (value < 1) {
    return 1;
  }
  if (value > 7) {
    return 7;
  }
  return Math.round(value);
};

const RatingRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isSubmitting,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void isSubmitting;
  void onExtrasChange;
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  const initialSavedValue = useMemo(() => {
    const raw = existingResponse?.rating_value;
    if (typeof raw === 'number') {
      return clampRating(raw);
    }
    return null;
  }, [existingResponse]);

  const [value, setValue] = useState<number | null>(initialSavedValue);

  const emitDraft = useCallback(
    (rating: number | null) => {
      const normalized = clampRating(rating);
      const hasSelection = typeof normalized === 'number';

      onHasUserInputChange?.(hasSelection);
      onValidityChange?.(hasSelection);

      if (!hasSelection || normalized == null) {
        onDraftChange(null);
        return;
      }

      const payload: ResponsePayload = {
        question: questionId,
        rating_value: normalized,
        text_value: String(normalized),
      };

      onDraftChange(payload);
    },
    [onDraftChange, onHasUserInputChange, onValidityChange, questionId],
  );

  useEffect(() => {
    setValue(initialSavedValue);

    if (initialSavedValue == null) {
      // Nothing is chosen until the learner chooses, so Continue waits for a number.
      onHasUserInputChange?.(false);
      onValidityChange?.(false);
      onDraftChange(null);
      return;
    }

    emitDraft(initialSavedValue);
  }, [emitDraft, initialSavedValue, onDraftChange, onHasUserInputChange, onValidityChange, questionId]);

  const handleChange = (nextValue: number) => {
    const normalized = clampRating(nextValue);
    setValue(normalized);
    emitDraft(normalized);
  };

  return (
    <RatingQuestion
      question={question}
      value={value}
      onChange={handleChange}
    />
  );
};

registerQuestionRenderer(['rating'], RatingRenderer);
